import { useState, useEffect } from 'react'
import type { HebcalEvent, ShabbatInfo } from '../types/electricity'

interface HebcalData {
  shabbat: ShabbatInfo
  holidays: HebcalEvent[]
  loading: boolean
}

const CACHE_KEY = 'hebcal-data'
const CACHE_DURATION = 6 * 60 * 60 * 1000 // 6 hours

interface CachedData {
  shabbat: ShabbatInfo
  holidays: HebcalEvent[]
  timestamp: number
}

export function useHebcal(): HebcalData {
  const [data, setData] = useState<HebcalData>({
    shabbat: { candleLighting: null, havdalah: null },
    holidays: [],
    loading: true,
  })

  useEffect(() => {
    // Check sessionStorage cache
    const cached = sessionStorage.getItem(CACHE_KEY)
    if (cached) {
      try {
        const parsed: CachedData = JSON.parse(cached)
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          setData({
            shabbat: {
              candleLighting: parsed.shabbat.candleLighting ? new Date(parsed.shabbat.candleLighting) : null,
              havdalah: parsed.shabbat.havdalah ? new Date(parsed.shabbat.havdalah) : null,
            },
            holidays: parsed.holidays,
            loading: false,
          })
          return
        }
      } catch {
        // Invalid cache, fetch fresh
      }
    }

    fetchHebcal()
  }, [])

  async function fetchHebcal() {
    try {
      const now = new Date()
      const year = now.getFullYear()
      const month = now.getMonth() + 1

      const url = `https://www.hebcal.com/hebcal?v=1&cfg=json&maj=on&min=off&mod=off&nx=off&year=${year}&month=${month}&ss=on&mf=off&c=on&geo=geoname&geonameid=293397&M=on&s=on`

      const res = await fetch(url)
      const json = await res.json()

      let candleLighting: Date | null = null
      let havdalah: Date | null = null
      const holidays: HebcalEvent[] = []

      for (const item of json.items || []) {
        if (item.category === 'candles' && !candleLighting) {
          candleLighting = new Date(item.date)
        }
        if (item.category === 'havdalah' && !havdalah) {
          havdalah = new Date(item.date)
        }
        if (item.category === 'holiday' && item.yomtov) {
          holidays.push({
            title: item.hebrew || item.title,
            date: item.date,
            category: item.category,
          })
        }
      }

      const result = {
        shabbat: { candleLighting, havdalah },
        holidays,
      }

      // Cache in sessionStorage
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        ...result,
        timestamp: Date.now(),
      }))

      setData({ ...result, loading: false })
    } catch {
      setData(prev => ({ ...prev, loading: false }))
    }
  }

  return data
}
